import { 
  ShieldCheck, Cloud, Cpu, Workflow, Briefcase, Settings
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services" },
  { label: "Industries", href: "/industries" },
  { label: "Cloud Solutions", href: "/services/cloud-solutions" }
];

export const ROTATING = [
  { text: "Fragmented systems → Unified operations in 90 days." },
  { text: "Manual processes → AI‑powered automation." },
  { text: "Static events → Immersive experiences." },
];

export const SERVICES = [
  {
    id: 'strategy',
    icon: Briefcase,
    title: "Strategy & Advisory",
    desc: "Technology roadmaps aligned to business goals; governance and stakeholder engagement for measurable, sustained results.",
    link: "/services/strategy-advisory"
  },
  {
    id: 'transformation',
    icon: Settings,
    title: "Digital & Systems Transformation",
    desc: "Modernise legacy systems, unify platforms and re‑engineer processes with AI/ML, CRM/ERP/IoT to cut cost and complexity.",
    link: "/services"
  },
  {
    id: 'cloud',
    icon: Cloud,
    title: "Cloud Solutions",
    desc: "Readiness, hybrid/multi‑cloud architecture, migration and management on AWS for scalable, secure, cost‑efficient ops.",
    link: "/services/cloud-solutions"
  },
  {
    id: 'data-ai',
    icon: Cpu,
    title: "Data, Analytics & AI Solutions",
    desc: "Data governance, pipelines/warehousing, predictive analytics and real‑time dashboards that turn data into decisions.",
    link: "/ai-data"
  },
  {
    id: 'cyber',
    icon: ShieldCheck,
    title: "Cybersecurity & Compliance",
    desc: "Risk assessments, Essential 8 controls, incident response and training to reduce risk and protect sensitive data.",
    link: "/services"
  },
  {
    id: 'change',
    icon: Workflow,
    title: "Change & Process Excellence",
    desc: "Project delivery, agile/hybrid methods, process optimisation and workforce upskilling to drive adoption and outcomes.",
    link: "/services"
  },
];

export const AI_SOLUTIONS = [
  { name: "FlowMind AI", desc: "Automated document understanding." },
  { name: "TapNChat AI", desc: "Conversational insights." },
  { name: "FormFlow AI", desc: "Intelligent form processing." },
  { name: "AI Engagement Bots", desc: "Smart chat for engagement & conversion." },
];

export const INDUSTRIES = [
  { slug: 'healthcare', name: "Healthcare", outcome: "Streamline clinical operations and data governance." },
  { slug: 'aged-care', name: "Aged Care & Disability", outcome: "Be ready for Support at Home reforms with digital readiness." },
  { slug: 'nfp', name: "Not‑for‑Profit", outcome: "Maximise impact through secure, efficient systems." },
  { slug: 'legal', name: "Legal & Professional Services", outcome: "Modernise case management and protect sensitive data." },
  { slug: 'education', name: "Education & E‑Learning", outcome: "Elevate experiences and manage data effectively." },
  { slug: 'startups', name: "Startups & Scaleups", outcome: "Build scalable platforms and culture‑proof growth." },
];

export const PROOFS = {
  logos: ["Client A", "Client B", "Client C"],
  stats: ["$50M+ projects delivered", "97% client satisfaction", "26% average productivity increase"],
  quotes: [
    { 
      q: "Moving to AWS with DD Consulting's guidance scaled our e-learning platform to handle 10x more students. Their expertise saved us months of trial and error.", 
      a: "David Park",
      company: "EduTech Innovations",
      position: "CTO",
      service: "Cloud Solutions",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face&auto=format"
    },
    {
      q: "DD Consulting's AI integration transformed our patient data management. We saw 40% faster processing and significantly improved compliance across all our clinics.",
      a: "Sarah Chen",
      company: "MedFlow Health Systems", 
      position: "Operations Director",
      service: "AI & Data Solutions",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face&auto=format"
    },
    {
      q: "Their strategic roadmap and digital transformation approach helped us reduce operational costs by 35% while improving service delivery to our clients.",
      a: "Michael Rodriguez",
      company: "Legal Partners Group",
      position: "Managing Partner", 
      service: "Strategy & Advisory",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face&auto=format"
    },
    {
      q: "The cybersecurity audit and Essential 8 implementation gave us confidence in our data protection. Their training program was comprehensive and practical.",
      a: "Emily Watson",
      company: "Community Care Network",
      position: "IT Manager",
      service: "Cybersecurity & Compliance", 
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face&auto=format"
    }
  ]
};

export const CASE_STUDIES = [
  { 
    title: "Transforming an aged‑care provider with AI & cloud", 
    summary: "Integrated cloud ERP + AI analytics cut costs by ~30% and improved compliance.", 
    href: "/case/aged-care-ai-cloud",
    image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=240&fit=crop&auto=format",
    tags: ["Cloud Migration", "AI Analytics"],
    tagColors: ["#1e64b6", "#b56d4a"],
    date: "2 days ago",
    type: "case-study"
  },
];

export const INSIGHTS = [
  { 
    title: "Why AI projects fail — and how to succeed", 
    excerpt: "People/process foundations and responsible AI.", 
    href: "/insights/why-ai-projects-fail",
    image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=400&h=240&fit=crop&auto=format",
    tags: ["AI Strategy", "Digital Transformation"],
    tagColors: ["#0891b2", "#059669"],
    date: "Yesterday",
    type: "insight"
  },
  {
    title: "Essential 8 cybersecurity framework for healthcare",
    excerpt: "A comprehensive guide to implementing cyber resilience in medical practices.",
    href: "/insights/essential-8-healthcare",
    image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&h=240&fit=crop&auto=format",
    tags: ["Cybersecurity", "Healthcare"],
    tagColors: ["#dc2626", "#ea580c"],
    date: "1 week ago",
    type: "insight"
  }
];

export const CONTACT = {
  address: "Sydney, NSW, Australia",
  office: "+61 2 8860 6593",
  phone: "+61 484 105 707",
  email: "info@ddconsult.tech",
  website: "https://ddconsult.com.au",
  linkedin: "https://www.linkedin.com/company/dd-consulting-au"
};

export const FAQ = [
  { q: 'How quickly can you deliver outcomes?', a: 'We aim for time‑to‑value in 6–12 weeks with phased roadmaps that de‑risk change.' },
  { q: 'Do you work with small teams and NFPs?', a: 'Yes. We tailor engagements to budget and capability—many projects start with discovery sprints.' },
  { q: 'How do you handle AI security & compliance?', a: 'We design secure patterns, implement data governance and protect your IP and client information.' },
  { q: 'What makes your AI projects succeed?', a: 'We integrate data, analytics and change with automation—so the model is the last step, not the first.' }
];